import React, {
  useState, useCallback, useEffect, useRef, useMemo,
} from 'react';
import { connect } from 'react-redux';
import {
  Grid, Divider, Button, Modal, Checkbox, Message, Icon,
} from 'semantic-ui-react';
import {
  useLocation,
} from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { DateInput, TimeInput } from 'semantic-ui-calendar-react';
import util from 'utils/utils';
import styled from 'styled-components';
import moment from 'moment';
import useTheme from 'theme/useTheme';

import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import TimezoneExplainer from 'components/lib/TimezoneExplainer';

import { CheckboxHeader } from 'components/lib/UI';
import IdeaBody from 'components/ideas/Body';
import FormatSection from './FormatSection';

const AssessmentExtraDetailsContainer = styled.div`
  margin-left: 7.5px;
  margin-bottom: 15px;
  padding: 5px 5px 5px 22.5px;
  border-left: 1px solid gray;
  display: flex;
  flex-direction: column;
  h5 {
    margin: 0;
    margin-bottom: 5px;
  }
  span {
    display: block;
    margin-bottom: 5px;
  }
`;

const SubmissionInputDiv = styled.div`
  display: ${({ theme }) => (theme.sizes.isMobile ? 'block' : 'flex')};
  flex-direction: row;
  align-items: center;
`;

const FormatEditor = (props) => {
  const {
    template = {},
    t,
    showClearOption,
    onSettingsUpdated,
    user,
    idea,
    onTemplateUpdated,
    onTemplateSaved = () => {},
    allowCreditsafeReport,
    challenge,
  } = props;
  const { body = [], assessmentDeadline, assessmentClosed } = template;
  const challengeId = challenge?._id;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editing, setEditing] = useState(true);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('00:00');
  const [assessmentDeadlineChanged, setAssessmentDeadlineChanged] = useState(false);
  const [assessmentEnabled, setAssessmentEnabled] = useState(body.some((section) => {
    const { assessment } = section;
    if (!assessment) return false;
    const { availableScore, notes, weighting } = assessment;
    return (availableScore || notes || weighting);
  }));
  const theme = useTheme();
  const formatRefs = useRef({});
  const setRef = useCallback((ref, itemId) => {
    formatRefs.current[itemId] = ref;
  }, []);

  const location = useLocation();
  const { search } = location;
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const queryFormatLocationId = useMemo(() => queryParams.get('formatLocationId') || '', [queryParams]);

  const scrollToFormatLocation = useCallback(() => {
    if (queryFormatLocationId) {
      const formatRef = formatRefs.current[queryFormatLocationId];
      if (formatRef) {
        formatRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [queryFormatLocationId]);

  useEffect(() => {
    scrollToFormatLocation();
  }, [scrollToFormatLocation]);

  const clearFormat = useCallback(() => {
    util.confirm(t('challenge.format.clear.confirmTitle'), t('challenge.format.clear.confirmInfo'))
      .then(() => {
        onTemplateUpdated([]);
        onTemplateSaved([], null);
      }).catch(() => { });
  }, [t, onTemplateUpdated, onTemplateSaved]);

  const addNewSection = useCallback(() => {
    onTemplateUpdated([...body, { name: 'New section', fields: [] }]);
  }, [onTemplateUpdated, body]);

  const deleteSection = useCallback((sectionIndex) => {
    util.confirm(t('challenge.format.sections.delete.title'), t('challenge.format.sections.delete.info'))
      .then(() => {
        onTemplateUpdated(body.filter((section, index) => index !== sectionIndex));
      }).catch(() => { });
  }, [t, onTemplateUpdated, body]);

  const moveSection = useCallback((sectionIndex, direction, dist = 1) => {
    const updatedBody = Object.assign([], body);
    const section = updatedBody[sectionIndex];
    updatedBody.splice(sectionIndex, 1);
    if (direction === 'up') updatedBody.splice(sectionIndex - dist, 0, section);
    else updatedBody.splice(sectionIndex + dist, 0, section);
    onTemplateUpdated(updatedBody);
  }, [body, onTemplateUpdated]);

  const editSectionInclude = useCallback((sectionIndex, include) => {
    const updatedBody = Object.assign([], body);
    if (include === 'custom') {
      delete updatedBody[sectionIndex].include;
      delete updatedBody[sectionIndex].type;
    } else if (include === 'edi') {
      delete updatedBody[sectionIndex].include;
      updatedBody[sectionIndex].type = 'edi';
    } else if (include === 'businessProfile') {
      delete updatedBody[sectionIndex].include;
      updatedBody[sectionIndex].type = 'businessProfile';
    } else { // A 6Cs section
      updatedBody[sectionIndex].include = include;
      delete updatedBody[sectionIndex].type;
    }
    onTemplateUpdated(updatedBody);
  }, [body, onTemplateUpdated]);

  const editSectionIsRequired = useCallback((sectionIndex, required) => {
    const updatedBody = Object.assign([], body);
    updatedBody[sectionIndex].isRequired = required;
    onTemplateUpdated(updatedBody);
  }, [body, onTemplateUpdated]);

  const editSectionName = useCallback((sectionIndex, name) => {
    const updatedBody = Object.assign([], body);
    updatedBody[sectionIndex].name = name;
    onTemplateUpdated(updatedBody);
  }, [body, onTemplateUpdated]);

  const editSectionAssessment = useCallback((sectionIndex, field, value) => {
    const updatedBody = Object.assign([], body);
    if (!updatedBody[sectionIndex].assessment) updatedBody[sectionIndex].assessment = {};
    updatedBody[sectionIndex].assessment[field] = value;
    onTemplateUpdated(updatedBody);
  }, [body, onTemplateUpdated]);

  const updateAssessmentDeadline = useCallback(() => {
    const dateAndTime = `${date} ${time}`;
    setAssessmentDeadlineChanged(false);
    onSettingsUpdated('assessmentDeadline', moment(dateAndTime).toDate(), true);
  }, [onSettingsUpdated, date, time]);

  const clearDeadline = useCallback(() => {
    onSettingsUpdated('assessmentDeadline', '', true);
  }, [onSettingsUpdated]);

  const reopenAssessment = useCallback(() => {
    onSettingsUpdated('assessmentDeadline', null, true);
    onSettingsUpdated('assessmentClosed', null, true);
  }, [onSettingsUpdated]);

  const editSectionField = useCallback((sectionIndex, fields) => {
    const updatedBody = Object.assign([], body);
    body[sectionIndex].fields = Object.assign([], fields);
    onTemplateUpdated(updatedBody);
  }, [body, onTemplateUpdated]);

  useEffect(() => {
    if (!assessmentDeadline) {
      setDate(moment().add(7, 'd').format('YYYY-MM-DD'));
    } else {
      setDate(moment(assessmentDeadline).format('YYYY-MM-DD'));
      setTime(moment(assessmentDeadline).format('HH:mm'));
    }
  }, [assessmentDeadline]);

  const toggleAssessment = useCallback((enabled) => {
    if (enabled) setAssessmentEnabled(true);
    else {
      util.confirm('Really turn off assessments? Any assessment weightings or notes you have added will be lost.')
        .then(() => {
          const updatedBody = body.map((section) => {
            if (section.assessment) {
              delete section.assessment;
            }
            return section;
          });
          onTemplateUpdated(updatedBody);
          onSettingsUpdated('assessmentDeadline', '');
          onSettingsUpdated('assessmentClosed', false);
          setAssessmentEnabled(false);
        }).catch(() => { });
    }
  }, [body, onTemplateUpdated, onSettingsUpdated]);

  if (!template) return null;
  const sixCsEnabled = user?.organisation?.enabledFeatures?.includes('6cs');
  return (
    <>
      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      >
        <div
          style={{ padding: 20 }}
        >
          <div
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%',
            }}
          >
            <div
              style={{ marginBottom: 20 }}
            >
              <h2>Idea preview</h2>
              <Checkbox
                label='Show editing view'
                checked={editing}
                onChange={() => setEditing((prevEditing) => !prevEditing)}
              />
            </div>
            <Button
              icon='close'
              onClick={() => setPreviewOpen(false)}
            />
          </div>
          <IdeaBody
            idea={idea}
            updateIdea={() => { }}
            isEditing={editing}
            user={user}
          />
        </div>
      </Modal>
      <Grid>
        <Grid.Column computer={16}>
          <CheckboxHeader
            plain
            header='Enable idea assessment'
            as='h5'
            checked={assessmentEnabled}
            onChange={(newAssessmentEnabled) => toggleAssessment(newAssessmentEnabled)}
          >
            <p>Enabling "assessments" allows assessors to provide per-section scores to ideas of this challenge. Users will not see the assessment details, and assessors can be assigned from the challenge's "Ideas" tab.</p>
          </CheckboxHeader>

          {assessmentEnabled
            ? (
              <AssessmentExtraDetailsContainer>
                <div>
                  <h5>Deadline</h5>
                  <span>Set an assessment deadline to automatically close assessments at a specific date. No further assessments can be created or submitted beyond this date.</span>
                  <TimezoneExplainer
                    time={time}
                    infoPrefix='The assessment will close at'
                    style={{ marginBottom: 10 }}
                  />
                  <SubmissionInputDiv theme={theme}>
                    <Checkbox
                      checked={!!assessmentDeadline}
                      label={t('challenge.settings.submission.deadline.deadline')}
                      onChange={() => (assessmentDeadline ? clearDeadline() : updateAssessmentDeadline())}
                    />
                    <DateInput
                      clearable
                      placeholder="Add a deadline"
                      value={date}
                      dateFormat="YYYY-MM-DD"
                      iconPosition="left"
                      size='small'
                      onChange={(e, { value }) => {
                        if (moment(value).isValid()) {
                          setAssessmentDeadlineChanged(true);
                          setDate(moment(value).format('YYYY-MM-DD'));
                        }
                      }}
                      onClear={() => setDate(null)}
                      style={{ marginLeft: 10, marginRight: 10, width: 175 }}
                    />
                    {' at '}
                    <TimeInput
                      clearable
                      name="time"
                      placeholder="Time"
                      value={time}
                      iconPosition="left"
                      onChange={(event, { value }) => {
                        setAssessmentDeadlineChanged(true);
                        setTime(value);
                      }}
                      onClear={() => setTime('00:00')}
                      style={{ marginLeft: 10, marginRight: 10, width: 175 }}
                    />
                    {(assessmentDeadline && assessmentDeadlineChanged)
                      ? (
                        <Button
                          content='Save deadline'
                          icon='save'
                          onClick={updateAssessmentDeadline}
                          style={{ marginRight: 10 }}
                          size='small'
                        />
                      ) : null
                    }

                  </SubmissionInputDiv>
                </div>
                {(assessmentClosed)
                  ? (
                    <Message warning>
                      <Icon name='check' />
                      Assessment is now closed for this challenge
                      <Button
                        content='Re-open assessment'
                        onClick={reopenAssessment}
                        floated='right'
                      />
                    </Message>
                  ) : (
                    <Message success>
                      Assessment is currently open for this challenge
                    </Message>
                  )
                }
              </AssessmentExtraDetailsContainer>
            ) : null
          }

          <CheckboxHeader
            plain
            header='Enable idea attachments'
            as='h5'
            checked={!template?.disableAttachments}
            onChange={(on) => onSettingsUpdated('disableAttachments', !on, true)}
          >
            <p>Enabling attachments allows idea-creators to upload arbitrary files from their computer along with their main idea content.</p>
          </CheckboxHeader>

          <Divider hidden section />
          <DragDropContext
            onDragEnd={(result) => {
              const { source, destination, type } = result;
              if (!destination) return;

              if (type === 'field') {
                const srcSection = parseInt(source.droppableId.split('-')[1], 10);
                const srcFields = body[srcSection].fields;

                const destSection = parseInt(destination.droppableId.split('-')[1], 10);
                const destFields = body[destSection].fields;

                if (srcSection === destSection && source.index === destination.index) {
                  return;
                }

                const field = srcFields.splice(source.index, 1)[0];
                destFields.splice(destination.index, 0, field);

                editSectionField(srcSection, srcFields);
                editSectionField(destSection, destFields);
              } else if (type === 'section') {
                if (source.index === destination.index) return;
                const direction = source.index > destination.index ? 'up' : 'down';
                const dist = Math.abs(source.index - destination.index);

                moveSection(source.index, direction, dist);
              }
            }}
          >
            <Droppable
              type='section'
              droppableId={'dropSection'}
            >
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {body.map((section, i) => (
                    <FormatSection
                      key={i}
                      section={section}
                      index={i}
                      body={body}
                      allowCreditsafeReport={allowCreditsafeReport}
                      deleteSection={deleteSection}
                      moveSection={moveSection}
                      editSectionInclude={editSectionInclude}
                      editSectionAssessment={editSectionAssessment}
                      editSectionName={editSectionName}
                      editSectionIsRequired={editSectionIsRequired}
                      editSectionField={editSectionField}
                      assessmentEnabled={assessmentEnabled}
                      challengeId={challengeId}
                      sixCsEnabled={sixCsEnabled}
                      setRef={setRef}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <Button size='large' style={{ width: '100%' }} fluid icon='plus' content={t('challenge.format.sections.new.title')} onClick={addNewSection} />
          <Divider />
        </Grid.Column>
        <Grid.Row style={{ position: 'sticky', bottom: 0 }}>
          {showClearOption && idea
            && <Grid.Column computer={8}>
              <Button fluid style={{ backgroundColor: util.adjustColour(theme.secondaryColour, 0.5), color: 'white' }} size='large' icon='eye' content='Preview format' onClick={() => setPreviewOpen(true)} />
            </Grid.Column>
          }
          {!!props.onTemplateSaved
            ? (
              <Grid.Column computer={8}>
                <Button fluid size='large' secondary icon='check' content={t('challenge.format.save')} onClick={() => onTemplateSaved()} />
              </Grid.Column>
            ) : null
          }
        </Grid.Row>
        <Grid.Row textAlign='right'>
          {showClearOption
            && <Grid.Column computer={16}>
              <Button size='large' basic color='orange' icon='ban' content={t('challenge.format.clear.button')} onClick={clearFormat} />
            </Grid.Column>
          }
        </Grid.Row>
      </Grid>
    </>
  );
};

const mapStateToProps = (state) => ({ user: state.user });
const FormatEditorContainer = connect(mapStateToProps, null)(FormatEditor);
export default withTranslation()(FormatEditorContainer);
